import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import MediaInput from "../../components/admin/MediaInput";

const inputCls =
  "w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white";

export default function AboutAdmin() {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/about").then(({ data }) => {
      setData(data);
      setTeam(data.team || []);
    });
    api.get("/faqs").then(({ data }) => setFaqs(data || []));
  }, []);

  if (!data) return <div className="overline text-[#8A8A93]">Loading…</div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addMember = () =>
    setTeam((t) => [...t, { name: "", role: "", bio: "", photo_url: "", visible: true }]);
  const updateMember = (idx, key, val) =>
    setTeam((t) => t.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const removeMember = (idx) => setTeam((t) => t.filter((_, i) => i !== idx));
  const moveMember = (idx, dir) =>
    setTeam((t) => {
      const j = idx + dir;
      if (j < 0 || j >= t.length) return t;
      const next = [...t];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const addFaq = () => setFaqs((f) => [...f, { question: "", answer: "" }]);
  const updateFaq = (idx, key, val) =>
    setFaqs((f) => f.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const removeFaq = (idx) => setFaqs((f) => f.filter((_, i) => i !== idx));
  const moveFaq = (idx, dir) =>
    setFaqs((f) => {
      const j = idx + dir;
      if (j < 0 || j >= f.length) return f;
      const next = [...f];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data, team };
      const { data: out } = await api.put("/about", payload);
      setData(out);
      setTeam(out.team || []);
      // Bulk-replace FAQ list — safe because it's a small, hand-curated set
      const faqPayload = faqs
        .filter((f) => (f.question || "").trim() && (f.answer || "").trim())
        .map((f, i) => ({
          question: f.question,
          answer: f.answer,
          order: i,
        }));
      const { data: faqOut } = await api.put("/faqs/bulk", faqPayload);
      setFaqs(faqOut || []);
      toast.success("About page saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">About</h1>
          <p className="text-sm text-[#8A8A93] mt-2">Edit every block of the public /about page. Toggle sections on or off without losing the content.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="about-save"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <SectionCard title="1. Opening line" visKey="opening_visible" data={data} set={set} testid="opening">
        <Field label="Overline (small label above headline)">
          <input value={data.opening_overline || ""} onChange={(e) => set("opening_overline", e.target.value)} className={inputCls} placeholder="About" />
        </Field>
        <Field label="Headline" hint="The big bold opening line. Carries the whole page.">
          <textarea rows={3} value={data.opening_line || ""} onChange={(e) => set("opening_line", e.target.value)} className={inputCls} placeholder="We don't chase attention. We build work that holds it." />
        </Field>
        <Field label="Italic-gold accent word" hint="The single word inside the headline rendered in gold italic. Leave empty to auto-style the last word.">
          <input value={data.opening_accent || ""} onChange={(e) => set("opening_accent", e.target.value)} className={inputCls} placeholder="holds" />
        </Field>
      </SectionCard>

      <SectionCard title="2. Short paragraph" visKey="intro_visible" data={data} set={set} testid="intro">
        <Field label="Section label">
          <input value={data.intro_label || ""} onChange={(e) => set("intro_label", e.target.value)} className={inputCls} placeholder="Who we are" />
        </Field>
        <Field label="Body">
          <textarea rows={4} value={data.intro_body || ""} onChange={(e) => set("intro_body", e.target.value)} className={inputCls} />
        </Field>
      </SectionCard>

      <SectionCard title="3. What we do" visKey="disciplines_visible" data={data} set={set} testid="disciplines">
        <Field label="Section label">
          <input value={data.disciplines_label || ""} onChange={(e) => set("disciplines_label", e.target.value)} className={inputCls} placeholder="What we do" />
        </Field>
        <Field label="Disciplines" hint="Comma-separated list. Each one renders on its own line as a large display heading.">
          <input value={data.disciplines || ""} onChange={(e) => set("disciplines", e.target.value)} className={inputCls} placeholder="Film, Creative Direction, Branding" />
        </Field>
      </SectionCard>

      <SectionCard title="4. Approach" visKey="approach_visible" data={data} set={set} testid="approach">
        <Field label="Section label">
          <input value={data.approach_label || ""} onChange={(e) => set("approach_label", e.target.value)} className={inputCls} placeholder="Approach" />
        </Field>
        <Field label="Body">
          <textarea rows={4} value={data.approach_body || ""} onChange={(e) => set("approach_body", e.target.value)} className={inputCls} />
        </Field>
      </SectionCard>

      <SectionCard title="5. Team" visKey="team_visible" data={data} set={set} testid="team">
        <Field label="Section label">
          <input value={data.team_label || ""} onChange={(e) => set("team_label", e.target.value)} className={inputCls} placeholder="The team" />
        </Field>
        <Field label="Intro paragraph (optional)">
          <textarea rows={2} value={data.team_intro || ""} onChange={(e) => set("team_intro", e.target.value)} className={inputCls} />
        </Field>
        <div className="space-y-3 pt-2">
          {team.length === 0 && (
            <p className="text-xs text-[#8A8A93] italic py-2">No team members yet. Add the first one below.</p>
          )}
          {team.map((m, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={`team-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="overline text-[#C9A050]">Member · {idx + 1}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={m.visible !== false} onChange={(e) => updateMember(idx, "visible", e.target.checked)} className="w-4 h-4 accent-[#C9A050]" />
                    <span className="overline text-xs">Show</span>
                  </label>
                  <button type="button" onClick={() => moveMember(idx, -1)} className="overline text-[#8A8A93] hover:text-white">↑</button>
                  <button type="button" onClick={() => moveMember(idx, 1)} className="overline text-[#8A8A93] hover:text-white">↓</button>
                  <button type="button" onClick={() => removeMember(idx)} data-testid={`team-remove-${idx}`} className="overline text-[#8A8A93] hover:text-red-400">Remove</button>
                </div>
              </div>
              <MediaInput
                value={m.photo_url || ""}
                onChange={(v) => updateMember(idx, "photo_url", v)}
                accept="image"
                folder="team"
                testid={`team-photo-${idx}`}
                placeholder="Photo URL (square or 4:5 portrait works best)"
                compact
              />
              <input placeholder="Name" value={m.name || ""} onChange={(e) => updateMember(idx, "name", e.target.value)} data-testid={`team-name-${idx}`} className={inputCls} />
              <input placeholder="Role / position" value={m.role || ""} onChange={(e) => updateMember(idx, "role", e.target.value)} data-testid={`team-role-${idx}`} className={inputCls} />
              <textarea rows={2} placeholder="One-liner bio (e.g. Cool boss but hot works)" value={m.bio || ""} onChange={(e) => updateMember(idx, "bio", e.target.value)} data-testid={`team-bio-${idx}`} className={inputCls} />
            </div>
          ))}
          <button type="button" onClick={addMember} data-testid="team-add" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add team member</button>
        </div>
      </SectionCard>

      <SectionCard title="6. Soft CTA" visKey="cta_visible" data={data} set={set} testid="cta">
        <Field label="CTA paragraph">
          <textarea rows={3} value={data.cta_body || ""} onChange={(e) => set("cta_body", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Button label">
          <input value={data.cta_button_label || ""} onChange={(e) => set("cta_button_label", e.target.value)} className={inputCls} placeholder="Start a project" />
        </Field>
        <Field label="Button link" hint="Leave empty to auto-route to your WhatsApp number from Settings → Contact, with the contact email as a fallback.">
          <input value={data.cta_button_href || ""} onChange={(e) => set("cta_button_href", e.target.value)} className={inputCls} placeholder="(optional) https://… or mailto:hello@..." />
        </Field>
      </SectionCard>

      <SectionCard title="7. Frequently asked" visKey="faq_visible" data={data} set={set} testid="faq">
        <Field label="Section label" hint="Small overline shown above the questions.">
          <input value={data.faq_label || ""} onChange={(e) => set("faq_label", e.target.value)} className={inputCls} placeholder="Frequently asked" />
        </Field>
        <Field label="Headline (optional)" hint="Optional big display heading above the FAQ list.">
          <input value={data.faq_heading || ""} onChange={(e) => set("faq_heading", e.target.value)} className={inputCls} placeholder="Common questions we hear." />
        </Field>
        <div className="space-y-3 pt-2">
          {faqs.length === 0 && (
            <p className="text-xs text-[#8A8A93] italic py-2">No FAQs yet. Add the first one below.</p>
          )}
          {faqs.map((f, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid={`faq-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="overline text-[#C9A050]">Q · {idx + 1}</span>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => moveFaq(idx, -1)} className="overline text-[#8A8A93] hover:text-white">↑</button>
                  <button type="button" onClick={() => moveFaq(idx, 1)} className="overline text-[#8A8A93] hover:text-white">↓</button>
                  <button type="button" onClick={() => removeFaq(idx)} data-testid={`faq-remove-${idx}`} className="overline text-[#8A8A93] hover:text-red-400">Remove</button>
                </div>
              </div>
              <input placeholder="Question" value={f.question || ""} onChange={(e) => updateFaq(idx, "question", e.target.value)} data-testid={`faq-q-${idx}`} className={inputCls} />
              <textarea rows={4} placeholder="Answer — 1–3 sentences works best. Written in your voice, not as marketing copy." value={f.answer || ""} onChange={(e) => updateFaq(idx, "answer", e.target.value)} data-testid={`faq-a-${idx}`} className={inputCls} />
            </div>
          ))}
          <button type="button" onClick={addFaq} data-testid="faq-add" className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2.5 transition-colors">+ Add FAQ</button>
        </div>
        <p className="text-xs text-[#8A8A93] mt-3">These also power the FAQ schema.org JSON-LD on /about — helps ChatGPT, Perplexity and Google's AI surface accurate answers about the studio.</p>
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, visKey, data, set, testid, children }) {
  const visible = data[visKey] !== false;
  return (
    <section className="border border-white/5 p-6 md:p-7" data-testid={`about-section-${testid}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => set(visKey, e.target.checked)}
            data-testid={`about-section-${testid}-visible`}
            className="w-4 h-4 accent-[#C9A050]"
          />
          <span className="overline text-xs">Show on /about</span>
        </label>
      </div>
      <div className={visible ? "" : "opacity-60"}>
        <div className={`space-y-5 ${visible ? "" : "[&_input:not([type=checkbox])]:opacity-50 [&_textarea]:opacity-50"}`}>{children}</div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#8A8A93] mt-2">{hint}</p>}
    </div>
  );
}
